"use client";

import React, {JSX, useCallback, useEffect, useRef, useState} from "react";

import {useSnapshot} from "valtio";

import {CheckAvailabilityInstance} from "@/features/rental/check-availability/check-availability.model";
import {CheckAvailabilityProps} from "@/features/rental/check-availability/check-availability.view";
import {BlockActions} from "@/features/rental/check-availability/parts/block-actions";
import {PriceSkeleton} from "@/features/rental/check-availability/parts/price-skeleton";

import {useTranslation} from "@/shared/i18n";
import {ButtonBase, CloseButton} from "@/shared/ui/buttons";
import {Typography} from "@/shared/ui/data-display/typography";
import {ArrowRightIcon} from "@/shared/ui/icons/navigation/arrow-right-icon";
import {Drawer} from "@/shared/ui/overlay/drawer";

type Props = Omit<CheckAvailabilityProps, "rating" | "reviewsCount" | "className" | "payload"> & {
    model: CheckAvailabilityInstance;
};

export function MobileViewport({pricePerNight, currencySymbol, model}: Props): JSX.Element {
    const {t} = useTranslation("booking");
    const {t: tRental} = useTranslation("rental");
    const {t: tCalendar} = useTranslation("calendar");
    const [isOpen, setIsOpen] = useState(false);
    const [showNextMonthArrow, setShowNextMonthArrow] = useState(false);
    const snapshot = useSnapshot(model);
    const openDrawer = (): void => setIsOpen(true);
    const closeDrawer = useCallback((): void => {
        setIsOpen(false);
        setShowNextMonthArrow(false);
    }, []);

    useEffect(() => {
        return model.events.on("noCheckoutDatesInView", (payload: {months: Array<{month: number; year: number}>}) => {
            const dr = model.dateRange;
            const matches = payload.months.some((m) => m.month === dr.viewMonth && m.year === dr.viewYear);
            if (matches) {
                setShowNextMonthArrow(true);
            }
        });
    }, [model]);

    // Hide arrow when month or dates change (skip first run so event can show it first)
    const isFirstRunRef = useRef(true);
    useEffect(() => {
        if (isFirstRunRef.current) {
            isFirstRunRef.current = false;
            return;
        }
        setShowNextMonthArrow(false);
    }, [snapshot.dateRange?.viewMonth, snapshot.dateRange?.viewYear, snapshot.dateRange?.value?.startDate]);

    const handleNextMonthArrowClick = useCallback((): void => {
        model.dateRange.nextMonth();
    }, [model]);

    // Close calendar drawer when inquiry form is closed after successful submission
    useEffect(() => {
        return model.events.on("inquirySubmitted", () => {
            setIsOpen(false);
        });
    }, [model]);

    return (
        <>
            <div className="check-availability-mobile" role="region" aria-label="Check availability bar">
                <div className="check-availability-mobile__inner">
                    {snapshot.hasDates ? (
                        <>
                            <button type="button" className="check-availability-mobile__quote" onClick={openDrawer}>
                                <PriceSkeleton loading={snapshot.isActionLoading} price={snapshot.totalPrice} />
                                <span className="check-availability-mobile__dates">{snapshot.formattedDateRange}</span>
                            </button>
                            <ButtonBase
                                variant="primary"
                                size="lg"
                                onClick={snapshot.hasDateError ? openDrawer : () => void model.submit()}
                                className="check-availability-mobile__checkout"
                                loading={snapshot.isActionLoading}
                            >
                                {snapshot.hasDateError ? t("changeDates") : snapshot.bookingType === "inquiry" ? t("propertyInquiry") : t("checkout")}
                            </ButtonBase>
                        </>
                    ) : (
                        <>
                            {pricePerNight && (
                                <div className="check-availability-mobile__price">
                                    <span className="check-availability-mobile__price-value">
                                        {currencySymbol} {pricePerNight}
                                    </span>
                                    <span className="check-availability-mobile__price-note">{t("avgPerNight")}</span>
                                </div>
                            )}
                            <ButtonBase variant="primary" size="lg" onClick={openDrawer} className="check-availability-mobile__action">
                                {t("checkAvailability")}
                            </ButtonBase>
                        </>
                    )}
                </div>
                {snapshot.hasDateError && snapshot.dateErrorMessage && (
                    <div className="check-availability-mobile__error">{tRental(snapshot.dateErrorMessage as Parameters<typeof tRental>[0])}</div>
                )}
                {snapshot.inquiryForm.submitFeedback.isSuccess && <div className="check-availability-mobile__inquiry-sent">{t("inquirySent")}</div>}
            </div>

            <Drawer open={isOpen} onClose={closeDrawer} direction="bottom" zIndex={999}>
                <div className="check-availability-drawer">
                    <div className="check-availability-drawer__header">
                        <Typography as="div" size={{desktop: 20, mobile: 18}} weight={700}>
                            {t("checkAvailability")}
                        </Typography>
                        <CloseButton onClick={closeDrawer}></CloseButton>
                    </div>
                    <BlockActions
                        hiddenAction
                        hideHeaderInputs
                        model={model}
                        className="check-availability-drawer__body"
                        rightOfCalendar={
                            showNextMonthArrow ? (
                                <button
                                    type="button"
                                    className="check-availability-drawer__next-month-arrow"
                                    onClick={handleNextMonthArrowClick}
                                    aria-label={tCalendar("nextMonth")}
                                >
                                    <ArrowRightIcon />
                                </button>
                            ) : null
                        }
                    />

                    <div className="check-availability-drawer__footer">
                        <ButtonBase
                            variant="primary"
                            size="lg-fixed"
                            width="full"
                            onClick={() => void model.submit()}
                            className="check-availability-drawer__submit"
                            loading={snapshot.isActionLoading}
                        >
                            {snapshot.bookingType === "inquiry" ? t("propertyInquiry") : t("checkout")}
                        </ButtonBase>
                        <Typography as="span" className="check-availability-drawer__note" size={12}>
                            {t("youWontBeCharged")}
                        </Typography>
                    </div>
                </div>
            </Drawer>
        </>
    );
}
